import { logError } from 'utils/logger';

import { getRequest } from '../apiRequests';

type PollerParams = {
  name: string;
  interval?: number;
  enableBackoffInterval?: boolean;

  callback?: Function;
  url?: string;
  useTimestamp?: boolean;
  timestamp?: string;

  cancelUrl?: string;
  requestID?: string;
  csrfToken?: string;
  serviceFunction?: () => Promise<any>;
};

export const MAX_RETRIES = 5;
export const DEFAULT_INTERVAL = 1000;

export class Poller {
  defaultInterval = DEFAULT_INTERVAL;

  retryCount = 0;

  maxRetry = MAX_RETRIES;

  currentTimer: any | null = null;

  name = '';

  callback?: Function | null = null;

  interval = this.defaultInterval;

  url = '';

  useTimestamp?: boolean = false;

  lastTimestamp: string | null = null;

  pause = false;

  currentUrl = '';

  cancelUrl?: string;

  requestID?: string;

  csrfToken?: string;

  runCounter = 0;

  serviceFunction;

  enableBackoffInterval = false;

  constructor({
    name,
    interval,
    enableBackoffInterval,
    url,
    callback,
    useTimestamp,
    timestamp,
    serviceFunction,
    cancelUrl,
    requestID,
    csrfToken,
  }: PollerParams) {
    if (!name) {
      throw new Error('Poller is missing a name');
    }

    if (!url && !serviceFunction) {
      throw new Error('Poller is missing a url or a service function');
    }

    this.name = name;
    this.callback = callback;
    this.interval = interval || this.interval;
    this.url = url || '';
    this.useTimestamp = useTimestamp;
    this.lastTimestamp = timestamp || null;
    this.serviceFunction = serviceFunction || null;
    this.enableBackoffInterval = !!enableBackoffInterval;
    this.cancelUrl = cancelUrl;
    this.requestID = requestID;
    this.csrfToken = csrfToken;
  }

  handleError(e: Error) {
    console.error(e);
    this.retryCount += 1;

    if (this.retryCount === this.maxRetry) {
      this.stop();
      logError(`Stopping [${this.name}]: Failed ${this.maxRetry} times.`);
    }

    return { error: e };
  }

  async repeaterFn() {
    if (this.pause) {
      return;
    }

    this.runCounter += 1;

    if (this.url) {
      this.currentUrl = this.url;
      if (this.useTimestamp) {
        this.currentUrl = `${this.url}${
          this.useTimestamp && this.lastTimestamp ? `?timestamp=${this.lastTimestamp}` : ''
        }`;
      }

      let responseJson: { data?: any; updated?: string; error?: Error };
      try {
        responseJson = await getRequest<null, any>(this.currentUrl);
      } catch (e: any) {
        responseJson = this.handleError(e);
      }

      if (this.useTimestamp && responseJson.updated) {
        this.lastTimestamp = responseJson.updated;
      }

      if (typeof this.callback === 'function' && !this.pause) {
        this.callback(responseJson);
      }
    } else if (this.serviceFunction) {
      try {
        await this.serviceFunction();
      } catch (e: any) {
        this.handleError(e);
      }
    }

    this.run();
  }

  getBackoffInterval(runCount: number) {
    // using linear back off for simplicity
    return runCount * 1000;
  }

  run() {
    if (this.runCounter === 0) {
      this.repeaterFn();
    } else {
      const interval = this.enableBackoffInterval ? this.getBackoffInterval(this.runCounter) : this.interval;
      this.currentTimer = setTimeout(this.repeaterFn.bind(this), interval);
    }
  }

  stop() {
    this.pause = true;
    if (this.currentTimer) {
      clearTimeout(this.currentTimer);
    }
  }
}
